import { defineStore } from 'pinia';
import { LocalStorage } from 'quasar';
import type { dynamicObj } from 'src/pages/assets';
import { getDocument } from 'src/sdk/api/document/document';
import { coa_dashboard_columns_setting } from 'src/constants';
import { getSavedColumns as getColumns, saveColumnOrder as saveColumns } from 'src/utils/columnSettingsUtil';
import type { OrderByDirection } from 'src/sdk/api/models/orderByDirection';

export const coaDashboardStore = defineStore('coa_dashboard_store', {
  state: () => ({
    loading: false,
    parent_id: '',
    display_data: [] as dynamicObj,
    total_count: 0,
    params: {
      search: '',
      status: '',
      sort_by: '',
      sort_order: '',
    },
    order_by: {} as dynamicObj,
    quickFilterMap: new Map(),
    table_columns: {
      document_id: {
        name: 'document_id',
        translation_key: 'document_id',
        actions: true,
        field: 'document_id',
        order: 0,
      },
      status: {
        name: 'status',
        translation_key: 'status',
        actions: true,
        field: 'status',
        order: 1,
      },
      filename: {
        name: 'filename',
        translation_key: 'filename',
        actions: true,
        field: 'filename',
        order: 2,
      },
      created_on: {
        name: 'created_on',
        translation_key: 'created_on',
        actions: true,
        field: 'created_on',
        order: 3,
      },
      sub_org_name: {
        name: 'sub_org_name',
        translation_key: 'sub_org_name',
        actions: true,
        field: 'sub_org_name',
        order: 4,
      },
    } as dynamicObj,
    pagination: {
      current_page: 1,
      total_pages: 0,
      page_size: 10,
    },
  }),
  actions: {
    resetPagination() {
      this.pagination = {
        current_page: 1,
        total_pages: 0,
        page_size: 10,
      };
    },
    resetFilter() {
      this.params = {
        search: '',
        status: '',
        sort_by: '',
        sort_order: '',
      };
      this.pagination = {
        current_page: 1,
        total_pages: 0,
        page_size: 10,
      };
      this.quickFilterMap.clear();
      this.order_by = {};
    },
    async getCoaData() {
      try {
        this.loading = true;

        const userDetail: any = LocalStorage.getItem('user_details');
        // Null safety check for user session
        if (!userDetail?.organisations?.[0]) {
          console.warn('[CoaDashboard] No organisation data available - user session may be incomplete');
          this.loading = false;
          return;
        }
        this.parent_id = userDetail.organisations[0].parent_org_id
          ?? userDetail.organisations[0].org_id;

        const offset =
          (this.pagination.current_page - 1) * this.pagination.page_size;

        const documentSDK = getDocument();
        const response: any = await documentSDK.dashboardDocumentDashboardsGet(
          {
            doc_type: 'certificate_of_analysis',
            org_id: this.parent_id,
            skip: offset,
            limit: this.pagination.page_size,
            keyword: this.params.search || undefined,
            status: this.params.status || undefined,
            order_by_field: this.params.sort_by || undefined,
            order_by_direction: (this.params.sort_order as OrderByDirection) || undefined,
          }
        );

        if (!response) {
          throw new Error('Failed to fetch COA data');
        }

        this.display_data = response?.data || [];
        this.pagination.total_pages = Math.ceil(
          (response.metadata?.total || 0) / this.pagination.page_size,
        );
        this.total_count = response?.count?.total || response?.metadata?.total || 0;
      } catch (err: any) {
        console.error('[CoaDashboard] Error fetching COA data:', err);
      } finally {
        this.loading = false;
      }
    },

    async getSavedColumns() {
      return await getColumns({
        settingKey: coa_dashboard_columns_setting,
        tableColumns: this.table_columns,
      });
    },

    async saveColumnOrder(columns: any[]) {
      await saveColumns(coa_dashboard_columns_setting, columns);
    },
  },
});
